import { ApiRequest, BaseResource, postItem, deleteItem, patchItem } from "utils/ApiUtils";
import buildQuery from 'odata-query';
import ODataUtil from './ODataUtil'


const get_contract_endpoint = "/ProjectContract";
const contract_endpoint = "/Contract";
const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds

class Contract extends BaseResource {
  constructor(args) {
    super(args, Contract.attributes);
  };

  static columns = [
    { title: "Organization", field: "Name_Organization" },
    { title: "OrganizationId", field: "OrganizationId_Organization", hidden: true },
    { title: "ContractorId", field: "ContractorId_Contractor", hidden: true},
    { title: "Contractor", field: "Description_Contractor" },
    { title: "Contract #", field: "ContractNumber_Contract" },
    { title: "NTP", field: "NtpDate_Contract", type: "date" },
    { title: "Expiration Date", field: "ExpDate_Contract", type: "date" },
    { title: "Current Day", field: "Current_NtpDate_Contract", sorting: false, filter: false },
    { title: "Day remaning", field: "Current_ExpDate_Contract", sorting: false, filter: false },
    { title: "Advertise Date", field: "AdvertiseDate_Contract", type: "date" },
    { title: "Award Date", field: "AwardDate_Contract", type: "date" },
    { title: "Bid Date", field: "BidDate_Contract", type: "date" },
    { title: "NTP Letter Date", field: "NtpLetterDate_Contract", type: "date" },
    { title: "Award Amount", field: "AwardAmount_Contract" },
    { title: "Retainage Cap", field: "RetainageCap_Contract" },
    { title: "Retainage Per Day", field: "RetainagePerPay_Contract" },
    { title: "Awards Days", field: "AwardsDays_Contract" },
    { title: "Duration", field: "TotalDayDuration_Contract", sorting: false, filter: false },
    { title: "ContractId", field: "ContractId", hidden: true },
    { title: "CreatorSystemUserId", field: "CreatorSystemUserId_Contract", hidden: true },
  ]

  static expand = [
    {
      "Contract": {
        "expand": [
          { "Organization": { "select": ["Name", "OrganizationId"] } },
          { "Contractor": { "select": ["Description", "ContractorId"] } },
        ]
      }
    },
    {
      "Contract": {
        select: [
          "ContractNumber",
          "NtpDate",
          "ExpDate",
          "AdvertiseDate",
          "AwardDate",
          "BidDate",
          "NtpLetterDate",
          "AwardAmount",
          "RetainageCap",
          "RetainagePerPay",
          "AwardsDays",
          "CreatorSystemUserId",
          "TotalDayDuration"
        ]
      }
    }
  ]

  static attributes = Contract.columns.map(r => r.field);
};

function convert_contracts_to_table_schema({ "value": items, "@odata.count": count }) {
  const data = items.map(r => {
    const contract = new Contract(r)
    contract.Current_NtpDate_Contract = Math.round(Math.abs((new Date().getTime() - new Date(r.Contract.NtpDate).getTime()) / (oneDay)))
    contract.Current_ExpDate_Contract = Math.round(((new Date(r.Contract.ExpDate).getTime() - new Date().getTime()) / (oneDay)))
    contract.Duration_ExpDate_Contract = Math.round(Math.abs((new Date(r.Contract.NtpDate).getTime() - new Date(r.Contract.ExpDate).getTime()) / (oneDay)))
    contract.Name_Organization = r.Contract.Organization ? r.Contract.Organization.Name : ""
    contract.OrganizationId_Organization = r.Contract.Organization ? r.Contract.Organization.OrganizationId : ""
    contract.Description_Contractor = r.Contract.Contractor ? r.Contract.Contractor.Description : ""
    contract.ContractorId_Contractor = r.Contract.Contractor ? r.Contract.Contractor.ContractorId : ""
    contract.ContractNumber_Contract = r.Contract ? r.Contract.ContractNumber : ""
    contract.NtpDate_Contract = r.Contract ? r.Contract.NtpDate : ""
    contract.ExpDate_Contract = r.Contract ? r.Contract.ExpDate : ""
    contract.TotalDayDuration_Contract = r.Contract ? r.Contract.TotalDayDuration : ""
    contract.AdvertiseDate_Contract = r.Contract ? r.Contract.AdvertiseDate : ""
    contract.AwardDate_Contract = r.Contract ? r.Contract.AwardDate : ""
    contract.BidDate_Contract = r.Contract ? r.Contract.BidDate : ""
    contract.NtpLetterDate_Contract = r.Contract ? r.Contract.NtpLetterDate : ""
    contract.AwardAmount_Contract = r.Contract ? r.Contract.AwardAmount : ""
    contract.RetainageCap_Contract = r.Contract ? r.Contract.RetainageCap : ""
    contract.RetainagePerPay_Contract = r.Contract ? r.Contract.RetainagePerPay : ""
    contract.AwardsDays_Contract = r.Contract ? r.Contract.AwardsDays : ""
    contract.CreatorSystemUserId_Contract = r.Contract ? r.Contract.CreatorSystemUserId : ""
    return contract;
  }
  );
  return {
    data, count
  };
};

async function getContracts(page, per_page, orderByInfo, orderByDirection, search, filters) {
  const { skip, count, top, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
    per_page, page, Contract.expand, orderByInfo, orderByDirection, filters)
  const filter = {};
  const isDefaults = filters.find(r => r.defaults);
  if (isDefaults) {
    const defaults = isDefaults.defaults;
    if (defaults.projectId) filter["ProjectId"] = { eq: { type: 'guid', value: defaults.projectId } };
  };
  // console.log(mappedFilter)
  const allFilters = mappedFilter.reduce((a, b) => ({ ...a, ...b }), filter);
  const params = buildQuery({ count, top, skip, orderBy, expand, filter: allFilters });
  const data = await ApiRequest(get_contract_endpoint, params);
  return convert_contracts_to_table_schema(data);
}

async function deleteContract(selectedProject, data) {
  const resourcePath = `${contract_endpoint}/Delete`;
  const params = `?projectId=${selectedProject}&contractId=${data.ContractId}`;
  const result = await deleteItem(resourcePath, params);
  return result;
};

async function addContracts(projectId, data, systemUserId) {
  data.CreatorSystemUserId = systemUserId;
  const resourcePath = `${contract_endpoint}/Add`;
  const params = `?projectId=${projectId}`;
  const result = await postItem(resourcePath, params, data);
  return result;
};

async function updateContracts(contractID, data) {
  const resourcePath = `${contract_endpoint}/Update`;
  const params = `?contractid=${contractID}`;
  const result = await patchItem(resourcePath, params, data);
  return result;
}

/*
// replace in component w/ generic func ODataUtil.unCoupleExpandsFromDefaults
const fieldMapping = (data) => {
  Object.keys(data).forEach((field) => {
    const updatedfield = field.split('_')[0]
    Contract.columns.forEach(r => {
      if (r.type === 'date' && r.field.split('_')[0].toLowerCase() === updatedfield.toLowerCase() && data[field] !== null) {
        data[field] = new Date(data[field]).toISOString()
      }
    });
    data[updatedfield] = data[field]
    delete data[field]
  })
  return data
}
*/

export { Contract, getContracts, updateContracts, addContracts, deleteContract };